/**
 * Lightbox ground plane — must use the same PlayCanvas module instance as SuperSplat (bundled).
 * Creating Mesh/Entity/Material from the host app's `playcanvas` import breaks the render path
 * (e.g. device.pushMarker is not a function) because two engine copies are mixed.
 */
import {
    ADDRESS_CLAMP_TO_EDGE,
    BLEND_NORMAL,
    CULLFACE_NONE,
    Entity,
    FILTER_LINEAR,
    FILTER_LINEAR_MIPMAP_LINEAR,
    Mat4,
    Mesh,
    MeshInstance,
    PIXELFORMAT_R8_G8_B8_A8,
    PlaneGeometry,
    Quat,
    Ray,
    StandardMaterial,
    Texture,
    Vec2,
    Vec3
} from 'playcanvas';

import type { GraphicsDevice } from 'playcanvas';

import type { Events } from './events';
import type { Scene } from './scene';

type LightboxOrientation = 'horizontal' | 'vertical';

/** Step-1 map plot ground plane — satellite mesh is anchored here when provided. */
export type LightboxGroundPlane = {
    normal: number[];
    pointOnPlane: number[];
};

interface LightboxLastOpts {
    orientation: LightboxOrientation;
    rotationZ: number;
    scale: number;
    position: number[];
    groundPlane: LightboxGroundPlane | null;
    /** Scene-space GCP tangent (length 3 each) — frozen map overlay uses this to avoid ambiguous twist. */
    overlayBasisEast: number[] | null;
    overlayBasisNorth: number[] | null;
}

interface LightboxPlaneState {
    entity: Entity;
    material: StandardMaterial;
    texture: Texture;
    mesh: Mesh;
    canvasSource?: HTMLCanvasElement;
    /** When true, plane is on the world layer and writes depth so splats composite “through” it like the map-plot ground gizmo. */
    depthOccludeBeforeSplats?: boolean;
    /** Local X/Z half extents of the plane mesh (before uniform `scale`), for hit testing. */
    meshHalfExtents: { x: number; z: number };
    lastOpts: LightboxLastOpts;
}

let lightboxPlaneState: LightboxPlaneState | null = null;

/**
 * Infinite grid (`infinite-grid-shader.ts`) draws the XZ ground at world y=0 (plane index 1).
 * `Entity#setPosition` uses world space; horizontal overlays must use this world Y so the mesh
 * sits on that plane (not splat AABB min.y, and not stale Firestore positions saved from older builds).
 */
const LIGHTBOX_WORLD_GROUND_Y = 0.08;

function worldPositionForHorizontalLightbox(x: number, z: number): [number, number, number] {
    return [x, LIGHTBOX_WORLD_GROUND_Y, z];
}

function projectXZOntoGroundPlane(x: number, z: number, gp: LightboxGroundPlane): [number, number, number] {
    const nx = gp.normal[0];
    const ny = gp.normal[1];
    const nz = gp.normal[2];
    const p0x = gp.pointOnPlane[0];
    const p0y = gp.pointOnPlane[1];
    const p0z = gp.pointOnPlane[2];
    if (Math.abs(ny) < 1e-6) {
        const [wx, wy, wz] = worldPositionForHorizontalLightbox(x, z);
        return [wx, wy, wz];
    }
    const y = p0y - (nx * (x - p0x) + nz * (z - p0z)) / ny;
    return [x, y, z];
}

function applyHorizontalPosition(
    orientation: LightboxOrientation,
    position: number[],
    groundPlane: LightboxGroundPlane | null | undefined
): [number, number, number] {
    if (orientation === 'vertical') {
        return [position[0], position[1], position[2]];
    }
    const gp = groundPlane ?? null;
    if (gp) {
        return projectXZOntoGroundPlane(position[0], position[2], gp);
    }
    return worldPositionForHorizontalLightbox(position[0], position[2]);
}

const _lbQuatAlign = new Quat();
const _lbQuatSpin = new Quat();
const _lbQuatOut = new Quat();
const _lbPlaneN = new Vec3();
const _lbHitWorld = new Vec3();

const _lbOvEx = new Vec3();
const _lbOvUy = new Vec3();
const _lbOvEz = new Vec3();
const _lbOvNr = new Vec3();
const _lbOvRotMat = new Mat4();
const _lbOvQuat = new Quat();

/**
 * PlaneGeometry: local +X / +Z tangent, +Y = face normal. GCP half-extents lie along solved east / north.
 * `setFromDirections(Vec3.UP, planeN)` only aligns the normal — twist around it is arbitrary on sloped planes.
 * Fixing columns from `basisEast` and plane normal removes misalignment after GCP solve.
 */
function quatFromGcpGroundOverlayBases(gp: LightboxGroundPlane, east: number[], north: number[]): Quat | null {
    _lbOvEx.set(east[0], east[1], east[2]);
    _lbOvUy.set(gp.normal[0], gp.normal[1], gp.normal[2]);
    _lbOvNr.set(north[0], north[1], north[2]);
    if (_lbOvEx.lengthSq() < 1e-20 || _lbOvUy.lengthSq() < 1e-20 || _lbOvNr.lengthSq() < 1e-20) return null;
    _lbOvEx.normalize();
    _lbOvUy.normalize();
    _lbOvNr.normalize();
    _lbOvEz.cross(_lbOvEx, _lbOvUy);
    if (_lbOvEz.lengthSq() < 1e-14) return null;
    _lbOvEz.normalize();
    if (_lbOvEz.dot(_lbOvNr) < 0) {
        _lbOvEz.mulScalar(-1);
    }
    /* Column-major 3×3 rotation: cols = mesh local (+X,+Y,+Z) → world (+east,+planeNormal,+tangent_Z). */
    _lbOvRotMat.set([
        _lbOvEx.x,
        _lbOvUy.x,
        _lbOvEz.x,
        0,
        _lbOvEx.y,
        _lbOvUy.y,
        _lbOvEz.y,
        0,
        _lbOvEx.z,
        _lbOvUy.z,
        _lbOvEz.z,
        0,
        0,
        0,
        0,
        1
    ]);
    return _lbOvQuat.setFromMat4(_lbOvRotMat);
}

function normalizeGroundPlaneInput(gp: unknown): LightboxGroundPlane | null {
    if (!gp || typeof gp !== 'object') return null;
    const o = gp as { normal?: unknown; pointOnPlane?: unknown };
    const n = o.normal;
    const p = o.pointOnPlane;
    if (!Array.isArray(n) || n.length !== 3 || !Array.isArray(p) || p.length !== 3) return null;
    if (![...n, ...p].every((x) => typeof x === 'number' && Number.isFinite(x))) return null;
    return {
        normal: [n[0], n[1], n[2]],
        pointOnPlane: [p[0], p[1], p[2]]
    };
}

function applyLightboxSurfaceMaterial(material: StandardMaterial, opacity: number): void {
    const op = Math.min(1, Math.max(0, opacity));
    material.opacity = op;
    /** Post-splat overlay: alpha-blend, no depth write — draws above the splat pass (lightbox overlay layer). */
    material.depthTest = true;
    material.depthWrite = false;
    material.blendType = BLEND_NORMAL;
    material.update();
}

/** Map-plot satellite plane: same pass as infinite grid / ground gizmo — participates in depth before splats. */
function applyLightboxGroundCanvasMaterial(material: StandardMaterial, opacity: number): void {
    const op = Math.min(1, Math.max(0, opacity));
    material.opacity = op;
    material.depthTest = true;
    material.depthWrite = true;
    material.blendType = BLEND_NORMAL;
    material.update();
}

const _lbRay = new Ray();
const _lbHit = new Vec3();
const _lbLocal = new Vec3();
const _lbInv = new Mat4();
/** World point for vertical drop onto lightbox map plane (project annotation → UV). */
const _lbProjWorld = new Vec3();

function getVolugraphScene(): Scene | null {
    const w = window as unknown as { scene?: Scene };
    return w.scene ?? null;
}

/** Plane lies in XZ; map image width → X, height → Z so aspect ratio matches the texture (no stretch). */
function meshHalfExtentsFromImageSize(width: number, height: number): { x: number; z: number } {
    const w = Number(width);
    const h = Number(height);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
        return { x: 1, z: 1 };
    }
    const r = w / h;
    if (r >= 1) {
        return { x: r, z: 1 };
    }
    return { x: 1, z: 1 / r };
}

/** Unit square plane (XZ half-extents 1×1); world size comes from uniform `scale` on the entity. */
function meshHalfExtentsForGroundOverlaySquare(): { x: number; z: number } {
    return { x: 1, z: 1 };
}

function getGroundOverlaySquareScaleImpl(): number | null {
    const scene = getVolugraphScene();
    if (!scene?.bound) return null;
    try {
        const e = scene.bound.halfExtents;
        const h = Math.max(e.x, e.z);
        if (!Number.isFinite(h) || h <= 0) return null;
        return h * 1.5;
    } catch {
        return null;
    }
}

function loadTextureFromUrl(device: GraphicsDevice, url: string): Promise<Texture> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const tex = new Texture(device, {
                width: img.width,
                height: img.height,
                format: PIXELFORMAT_R8_G8_B8_A8,
                mipmaps: true,
                minFilter: FILTER_LINEAR_MIPMAP_LINEAR,
                magFilter: FILTER_LINEAR,
                addressU: ADDRESS_CLAMP_TO_EDGE,
                addressV: ADDRESS_CLAMP_TO_EDGE,
                name: 'volugraphLightbox'
            });
            tex.setSource(img);
            resolve(tex);
        };
        img.onerror = () => reject(new Error('Lightbox image failed to load'));
        img.src = url;
    });
}

function destroyLightboxPlaneInternal(): void {
    const s = lightboxPlaneState;
    lightboxPlaneState = null;
    if (!s) return;
    try {
        s.entity?.destroy();
    } catch {
        /* ignore */
    }
    try {
        s.mesh?.destroy();
    } catch {
        /* ignore */
    }
    try {
        s.material?.destroy();
    } catch {
        /* ignore */
    }
    try {
        s.texture?.destroy();
    } catch {
        /* ignore */
    }
}

function getLightboxDefaultPosition(): Vec3 | null {
    const scene = getVolugraphScene();
    if (!scene?.bound) return null;
    try {
        const b = scene.bound;
        const c = b.center;
        const [wx, wy, wz] = worldPositionForHorizontalLightbox(c.x, c.z);
        return new Vec3(wx, wy, wz);
    } catch {
        return null;
    }
}

async function lightboxPlaneCreateImpl(opts: {
    imageUrl: string;
    orientation?: LightboxOrientation;
    position?: number[];
    rotationZ?: number;
    scale?: number;
    opacity?: number;
    groundPlane?: unknown;
}): Promise<boolean> {
    const scene = getVolugraphScene();
    if (!scene?.app?.graphicsDevice || !scene.contentRoot || !scene.lightboxOverlayLayer) return false;
    const imageUrl = opts?.imageUrl;
    if (!imageUrl || typeof imageUrl !== 'string') return false;

    destroyLightboxPlaneInternal();

    const device = scene.app.graphicsDevice;
    const texture = await loadTextureFromUrl(device, imageUrl);

    const { x: halfX, z: halfZ } = meshHalfExtentsFromImageSize(texture.width, texture.height);
    const geom = new PlaneGeometry({
        halfExtents: new Vec2(halfX, halfZ),
        widthSegments: 1,
        lengthSegments: 1
    });
    const mesh = Mesh.fromGeometry(device, geom);

    const material = new StandardMaterial();
    material.cull = CULLFACE_NONE;
    material.diffuse.set(0, 0, 0);
    material.emissive.set(1, 1, 1);
    material.emissiveMap = texture;
    material.useLighting = false;
    const opacity =
        typeof opts.opacity === 'number' && Number.isFinite(opts.opacity)
            ? Math.min(1, Math.max(0, opts.opacity))
            : 1;
    applyLightboxSurfaceMaterial(material, opacity);

    const meshInstance = new MeshInstance(mesh, material);
    const entity = new Entity('volugraphLightboxPlane', scene.app);
    entity.addComponent('render', {
        meshInstances: [meshInstance],
        layers: [scene.lightboxOverlayLayer.id]
    });

    scene.contentRoot.addChild(entity);

    let pos = Array.isArray(opts.position) && opts.position.length === 3 ? [...opts.position] : null;
    if (!pos || !pos.every((n) => typeof n === 'number' && Number.isFinite(n))) {
        const def = getLightboxDefaultPosition();
        pos = def ? [def.x, def.y, def.z] : worldPositionForHorizontalLightbox(0, 0);
    }
    const orient: LightboxOrientation = opts.orientation === 'vertical' ? 'vertical' : 'horizontal';
    const gpCreate = normalizeGroundPlaneInput(opts?.groundPlane) ?? null;
    pos = [...applyHorizontalPosition(orient, pos, gpCreate)] as number[];

    lightboxPlaneState = {
        entity,
        material,
        texture,
        mesh,
        meshHalfExtents: { x: halfX, z: halfZ },
        lastOpts: {
            orientation: orient,
            rotationZ:
                typeof opts.rotationZ === 'number' && Number.isFinite(opts.rotationZ) ? opts.rotationZ : 0,
            scale: typeof opts.scale === 'number' && Number.isFinite(opts.scale) && opts.scale > 0 ? opts.scale : 2,
            position: pos,
            groundPlane: gpCreate,
            overlayBasisEast: null,
            overlayBasisNorth: null
        }
    };

    lightboxPlaneUpdateImpl({
        orientation: lightboxPlaneState.lastOpts.orientation,
        position: lightboxPlaneState.lastOpts.position,
        rotationZ: lightboxPlaneState.lastOpts.rotationZ,
        scale: lightboxPlaneState.lastOpts.scale,
        opacity: opts.opacity,
        groundPlane: gpCreate
    });

    scene.forceRender = true;
    return true;
}

async function lightboxPlaneCreateFromCanvasImpl(
    canvas: HTMLCanvasElement,
    opts: {
        orientation?: LightboxOrientation;
        position?: number[];
        rotationZ?: number;
        scale?: number;
        opacity?: number;
        /** When true, use a 1:1 plane mesh; pass `scale` from `getGroundOverlaySquareScale` (or larger) for world size. */
        forceSquare?: boolean;
        /** When set, local X/Z half-extents of the plane mesh (before uniform `scale`). Overrides `forceSquare` / image aspect. */
        meshHalfExtents?: { x: number; z: number };
        /**
         * When true, do not retain the canvas for `lightboxPlaneSyncCanvasTexture` — texture is fixed after create (frozen raster).
         */
        staticTexture?: boolean;
        /** When set with `overlayBasisNorth`, orients frozen GCP satellite quad (removes twist vs normal-only align). */
        overlayBasisEast?: number[];
        overlayBasisNorth?: number[];
        groundPlane?: unknown;
    }
): Promise<boolean> {
    const scene = getVolugraphScene();
    if (!scene?.app?.graphicsDevice || !scene.contentRoot || !scene.worldLayer) {
        if (!scene?.app?.graphicsDevice) {
            console.warn('[Lightbox] lightboxPlaneCreateFromCanvas: missing scene.app.graphicsDevice');
        } else if (!scene.contentRoot) {
            console.warn('[Lightbox] lightboxPlaneCreateFromCanvas: missing scene.contentRoot');
        } else {
            console.warn('[Lightbox] lightboxPlaneCreateFromCanvas: missing scene.worldLayer');
        }
        return false;
    }
    if (!canvas || typeof canvas.width !== 'number') return false;

    destroyLightboxPlaneInternal();

    const device = scene.app.graphicsDevice;
    const texture = new Texture(device, {
        width: canvas.width,
        height: canvas.height,
        format: PIXELFORMAT_R8_G8_B8_A8,
        mipmaps: false,
        minFilter: FILTER_LINEAR,
        magFilter: FILTER_LINEAR,
        addressU: ADDRESS_CLAMP_TO_EDGE,
        addressV: ADDRESS_CLAMP_TO_EDGE,
        name: 'volugraphLightboxCanvas'
    });
    texture.setSource(canvas);

    const me = opts?.meshHalfExtents;
    const explicitMe =
        me &&
        typeof me.x === 'number' &&
        typeof me.z === 'number' &&
        Number.isFinite(me.x) &&
        Number.isFinite(me.z) &&
        me.x > 0 &&
        me.z > 0;
    const forceSquare = opts?.forceSquare === true;
    const { x: halfX, z: halfZ } = explicitMe
        ? { x: me.x, z: me.z }
        : forceSquare
          ? meshHalfExtentsForGroundOverlaySquare()
          : meshHalfExtentsFromImageSize(canvas.width, canvas.height);
    const geom = new PlaneGeometry({
        halfExtents: new Vec2(halfX, halfZ),
        widthSegments: 1,
        lengthSegments: 1
    });
    const mesh = Mesh.fromGeometry(device, geom);

    const material = new StandardMaterial();
    material.cull = CULLFACE_NONE;
    material.diffuse.set(0, 0, 0);
    material.emissive.set(1, 1, 1);
    material.emissiveMap = texture;
    material.useLighting = false;
    const opacity =
        typeof opts?.opacity === 'number' && Number.isFinite(opts.opacity)
            ? Math.min(1, Math.max(0, opts.opacity))
            : 1;
    applyLightboxGroundCanvasMaterial(material, opacity);

    const meshInstance = new MeshInstance(mesh, material);
    const entity = new Entity('volugraphLightboxPlane', scene.app);
    entity.addComponent('render', {
        meshInstances: [meshInstance],
        layers: [scene.worldLayer.id]
    });

    scene.contentRoot.addChild(entity);

    let pos = Array.isArray(opts?.position) && opts.position.length === 3 ? [...opts.position] : null;
    if (!pos || !pos.every((n) => typeof n === 'number' && Number.isFinite(n))) {
        const def = getLightboxDefaultPosition();
        pos = def ? [def.x, def.y, def.z] : worldPositionForHorizontalLightbox(0, 0);
    }
    const orientCanvas: LightboxOrientation = opts?.orientation === 'vertical' ? 'vertical' : 'horizontal';
    const gpFromCanvas = normalizeGroundPlaneInput(opts?.groundPlane) ?? null;
    pos = [...applyHorizontalPosition(orientCanvas, pos, gpFromCanvas)] as number[];

    const obe = opts?.overlayBasisEast;
    const obn = opts?.overlayBasisNorth;
    let overlayBasisEast: number[] | null = null;
    let overlayBasisNorth: number[] | null = null;
    if (
        Array.isArray(obe) &&
        obe.length === 3 &&
        obe.every((x) => typeof x === 'number' && Number.isFinite(x)) &&
        Array.isArray(obn) &&
        obn.length === 3 &&
        obn.every((x) => typeof x === 'number' && Number.isFinite(x))
    ) {
        overlayBasisEast = [obe[0], obe[1], obe[2]];
        overlayBasisNorth = [obn[0], obn[1], obn[2]];
    }

    lightboxPlaneState = {
        entity,
        material,
        texture,
        mesh,
        canvasSource: opts?.staticTexture === true ? undefined : canvas,
        depthOccludeBeforeSplats: true,
        meshHalfExtents: { x: halfX, z: halfZ },
        lastOpts: {
            orientation: orientCanvas,
            rotationZ:
                typeof opts?.rotationZ === 'number' && Number.isFinite(opts.rotationZ) ? opts.rotationZ : 0,
            scale: typeof opts?.scale === 'number' && Number.isFinite(opts.scale) && opts.scale > 0 ? opts.scale : 2,
            position: pos,
            groundPlane: gpFromCanvas,
            overlayBasisEast,
            overlayBasisNorth
        }
    };

    lightboxPlaneUpdateImpl({
        orientation: lightboxPlaneState.lastOpts.orientation,
        position: lightboxPlaneState.lastOpts.position,
        rotationZ: lightboxPlaneState.lastOpts.rotationZ,
        scale: lightboxPlaneState.lastOpts.scale,
        opacity: opts?.opacity,
        groundPlane: gpFromCanvas
    });

    scene.forceRender = true;
    return true;
}

function lightboxPlaneUpdateImpl(patch: {
    orientation?: LightboxOrientation;
    position?: number[];
    rotationZ?: number;
    scale?: number;
    opacity?: number;
    groundPlane?: unknown | null;
}): void {
    const s = lightboxPlaneState;
    const scene = getVolugraphScene();
    if (!s?.entity || !scene) return;

    const prev = s.lastOpts;
    const orientation =
        patch.orientation === 'vertical' || patch.orientation === 'horizontal'
            ? patch.orientation
            : prev.orientation;
    const rz =
        typeof patch.rotationZ === 'number' && Number.isFinite(patch.rotationZ) ? patch.rotationZ : prev.rotationZ;
    const sc =
        typeof patch.scale === 'number' && Number.isFinite(patch.scale) && patch.scale > 0 ? patch.scale : prev.scale;
    let gp: LightboxGroundPlane | null = prev.groundPlane;
    if (patch.groundPlane !== undefined) {
        gp = patch.groundPlane === null ? null : normalizeGroundPlaneInput(patch.groundPlane);
    }
    let pos = prev.position;
    if (Array.isArray(patch.position) && patch.position.length === 3) {
        const [x, y, z] = patch.position;
        if ([x, y, z].every((n) => typeof n === 'number' && Number.isFinite(n))) {
            pos = [x, y, z];
        }
    }
    pos = [...applyHorizontalPosition(orientation, pos, gp)] as number[];

    s.lastOpts = {
        orientation,
        rotationZ: rz,
        scale: sc,
        position: pos,
        groundPlane: gp,
        overlayBasisEast: prev.overlayBasisEast,
        overlayBasisNorth: prev.overlayBasisNorth
    };

    const nextOpacity =
        patch.opacity != null && Number.isFinite(patch.opacity)
            ? Math.min(1, Math.max(0, patch.opacity))
            : s.material.opacity;
    if (s.depthOccludeBeforeSplats) {
        applyLightboxGroundCanvasMaterial(s.material, nextOpacity);
    } else {
        applyLightboxSurfaceMaterial(s.material, nextOpacity);
    }

    const OP_EPS = 0.01;
    const renderComp = s.entity.render;
    if (renderComp) {
        renderComp.enabled = nextOpacity > OP_EPS;
    }

    if (orientation === 'vertical') {
        s.entity.setLocalEulerAngles(-90, rz, 0);
    } else if (gp) {
        const useGcpFrame =
            s.depthOccludeBeforeSplats === true &&
            s.lastOpts.overlayBasisEast &&
            s.lastOpts.overlayBasisNorth;
        const qGcp =
            useGcpFrame
                ? quatFromGcpGroundOverlayBases(
                      gp,
                      s.lastOpts.overlayBasisEast,
                      s.lastOpts.overlayBasisNorth
                  )
                : null;
        if (qGcp) {
            if (Math.abs(rz) > 1e-7) {
                _lbPlaneN.set(gp.normal[0], gp.normal[1], gp.normal[2]);
                _lbPlaneN.normalize();
                _lbQuatSpin.setFromAxisAngle(_lbPlaneN, rz);
                _lbQuatOut.mul2(_lbQuatSpin, qGcp);
                s.entity.setRotation(_lbQuatOut);
            } else {
                s.entity.setRotation(qGcp);
            }
        } else {
            _lbPlaneN.set(gp.normal[0], gp.normal[1], gp.normal[2]);
            _lbPlaneN.normalize();
            _lbQuatAlign.setFromDirections(Vec3.UP, _lbPlaneN);
            _lbQuatSpin.setFromAxisAngle(_lbPlaneN, rz);
            _lbQuatOut.mul2(_lbQuatSpin, _lbQuatAlign);
            s.entity.setRotation(_lbQuatOut);
        }
    } else {
        s.entity.setLocalEulerAngles(0, rz, 0);
    }

    s.entity.setPosition(pos[0], pos[1], pos[2]);
    s.entity.setLocalScale(sc, sc, sc);
    scene.forceRender = true;
}

function lightboxPlaneRemoveImpl(): void {
    const scene = getVolugraphScene();
    destroyLightboxPlaneInternal();
    if (scene) scene.forceRender = true;
}

function lightboxPlaneSyncCanvasTextureImpl(): void {
    const scene = getVolugraphScene();
    const s = lightboxPlaneState;
    if (!scene?.app?.graphicsDevice || !s?.material || !s.canvasSource) return;
    const c = s.canvasSource;
    const device = scene.app.graphicsDevice;
    try {
        const cw = c.width;
        const ch = c.height;
        if (cw <= 0 || ch <= 0) return;

        if (!s.texture || s.texture.width !== cw || s.texture.height !== ch) {
            try {
                s.texture?.destroy();
            } catch {
                /* ignore */
            }
            const texture = new Texture(device, {
                width: cw,
                height: ch,
                format: PIXELFORMAT_R8_G8_B8_A8,
                mipmaps: false,
                minFilter: FILTER_LINEAR,
                magFilter: FILTER_LINEAR,
                addressU: ADDRESS_CLAMP_TO_EDGE,
                addressV: ADDRESS_CLAMP_TO_EDGE,
                name: 'volugraphLightboxCanvas'
            });
            texture.setSource(c);
            s.material.emissiveMap = texture;
            s.material.update();
            s.texture = texture;
        } else {
            s.texture.setSource(c);
        }
        scene.forceRender = true;
    } catch {
        /* ignore */
    }
}

function lightboxMapHitTestImpl(clientX: number, clientY: number): { world: Vec3; localX: number; localZ: number } | null {
    const scene = getVolugraphScene();
    const s = lightboxPlaneState;
    if (!scene?.camera?.getRay || !s?.entity) return null;
    if (s.lastOpts.orientation !== 'horizontal') return null;
    const canvas = scene.app.graphicsDevice.canvas;
    const rect = canvas.getBoundingClientRect();
    const sx = clientX - rect.left;
    const sy = clientY - rect.top;
    scene.camera.getRay(sx, sy, _lbRay);
    const origin = _lbRay.origin;
    const dir = _lbRay.direction;

    _lbPlaneN.copy(Vec3.UP);
    s.entity.getRotation().transformVector(_lbPlaneN, _lbPlaneN);
    _lbPlaneN.normalize();
    const planePt = s.entity.getPosition();
    _lbHit.copy(planePt).sub(origin);
    const tNum = _lbHit.dot(_lbPlaneN);
    const denom = dir.dot(_lbPlaneN);
    if (Math.abs(denom) < 1e-8) return null;
    const t = tNum / denom;
    if (t <= 0) return null;
    _lbHitWorld.copy(dir).mulScalar(t).add(origin);
    const worldHit = new Vec3().copy(_lbHitWorld);
    _lbInv.copy(s.entity.getWorldTransform()).invert();
    _lbInv.transformPoint(worldHit, _lbLocal);
    const hx = s.meshHalfExtents.x;
    const hz = s.meshHalfExtents.z;
    const halfX = s.lastOpts.scale * hx;
    const halfZ = s.lastOpts.scale * hz;
    if (Math.abs(_lbLocal.x) <= halfX && Math.abs(_lbLocal.z) <= halfZ) {
        return { world: worldHit, localX: _lbLocal.x, localZ: _lbLocal.z };
    }
    return null;
}

/**
 * Vertical world drop (pin XZ, plane Y) → local quad → normalised UV for MapLibre `unproject`.
 * `maxMult` caps extrapolation: reject if |u-0.5| or |v-0.5| exceeds maxMult/2 (e.g. 2 → u,v in [-0.5, 1.5]).
 */
function lightboxProjectWorldToMapUVImpl(
    wx: number,
    wy: number,
    wz: number,
    maxMult: number
): { u: number; v: number } | null {
    const s = lightboxPlaneState;
    if (!s?.entity) return null;
    if (s.lastOpts.orientation !== 'horizontal') return null;
    _lbPlaneN.copy(Vec3.UP);
    s.entity.getRotation().transformVector(_lbPlaneN, _lbPlaneN);
    _lbPlaneN.normalize();
    const planePt = s.entity.getPosition();
    _lbProjWorld.set(wx, wy, wz);
    _lbHit.copy(_lbProjWorld).sub(planePt);
    const dist = _lbHit.dot(_lbPlaneN);
    _lbHit.copy(_lbPlaneN).mulScalar(dist);
    _lbProjWorld.sub(_lbHit);
    _lbInv.copy(s.entity.getWorldTransform()).invert();
    _lbInv.transformPoint(_lbProjWorld, _lbLocal);
    const hx = s.meshHalfExtents.x * s.lastOpts.scale;
    const hz = s.meshHalfExtents.z * s.lastOpts.scale;
    if (!Number.isFinite(hx) || !Number.isFinite(hz) || hx <= 0 || hz <= 0) return null;
    const u = _lbLocal.x / (2 * hx) + 0.5;
    const v = _lbLocal.z / (2 * hz) + 0.5;
    const m = Number.isFinite(maxMult) && maxMult > 0 ? maxMult : 2;
    const halfRange = m * 0.5;
    if (Math.abs(u - 0.5) > halfRange || Math.abs(v - 0.5) > halfRange) return null;
    return { u, v };
}

function getLightboxMaxScaleFromSplatBoundImpl(): number | null {
    const scene = getVolugraphScene();
    if (!scene?.bound) return null;
    try {
        const e = scene.bound.halfExtents;
        return Math.max(e.x, e.z) * 2;
    } catch {
        return null;
    }
}

/**
 * Register invoke targets for the React bridge — all PlayCanvas objects are created here only.
 */
export function registerVolugraphLightboxPlane(events: Events): void {
    events.function('volugraph.lightboxPlaneCreate', (opts: {
        imageUrl: string;
        orientation?: LightboxOrientation;
        position?: number[];
        rotationZ?: number;
        scale?: number;
        opacity?: number;
        groundPlane?: unknown;
    }) => lightboxPlaneCreateImpl(opts));
    events.function(
        'volugraph.lightboxPlaneCreateFromCanvas',
        (canvas: HTMLCanvasElement, opts: {
            orientation?: LightboxOrientation;
            position?: number[];
            rotationZ?: number;
            scale?: number;
            opacity?: number;
            forceSquare?: boolean;
            meshHalfExtents?: { x: number; z: number };
            staticTexture?: boolean;
            groundPlane?: unknown;
            overlayBasisEast?: number[];
            overlayBasisNorth?: number[];
        }) => lightboxPlaneCreateFromCanvasImpl(canvas, opts)
    );
    events.function(
        'volugraph.lightboxPlaneUpdate',
        (patch: {
            orientation?: LightboxOrientation;
            position?: number[];
            rotationZ?: number;
            scale?: number;
            opacity?: number;
            groundPlane?: unknown | null;
        }) => {
            lightboxPlaneUpdateImpl(patch);
        }
    );
    events.function('volugraph.lightboxPlaneRemove', () => {
        lightboxPlaneRemoveImpl();
    });
    events.function('volugraph.lightboxPlaneIsLoaded', () => !!lightboxPlaneState?.entity);
    events.function('volugraph.lightboxPlaneSyncCanvasTexture', () => {
        lightboxPlaneSyncCanvasTextureImpl();
    });
    events.function('volugraph.lightboxMapHitTest', (clientX: number, clientY: number) =>
        lightboxMapHitTestImpl(clientX, clientY)
    );
    events.function(
        'volugraph.lightboxProjectWorldToMapUV',
        (wx: number, wy: number, wz: number, maxMult?: number) =>
            lightboxProjectWorldToMapUVImpl(wx, wy, wz, maxMult ?? 2)
    );
    events.function('volugraph.getLightboxDefaultPosition', () => {
        const v = getLightboxDefaultPosition();
        return v ? { x: v.x, y: v.y, z: v.z } : null;
    });
    events.function('volugraph.getLightboxMaxScaleFromSplatBound', () => getLightboxMaxScaleFromSplatBoundImpl());
    events.function('volugraph.getGroundOverlaySquareScale', () => getGroundOverlaySquareScaleImpl());
}
